import { exportEncryptionKey } from '@samedi/crypto-js/crypto'
import { asymmetricEncrypt } from '@samedi/crypto-js/cryptor'

import { generateSessionKey, loadSessionKey } from '@samedi/crypto-js/subtle/symmetric'

import { hideLoader, showLoader } from 'util/loader'

import ErrorMessageController from '../../controllers/document_upload/error_message_controller'

import { getPatientKeyOnce, encryptSessionData } from '../../encryption/encryption'
import { CSRFToken } from '../../util/csrfToken'
import { stripHTML } from '../messages'

import uploadFile from '../uploadFiles'

interface InstitutionPublicKey {
  id: string
  public_exponent: string
  modulus: string
}

interface MessagePayload {
  data: {
    message: {
      encrypted_body: string
      practice_id: string
      session_key: string
      session_key_patient_public_key_external_id: string
    }
    recipient_data: {
      encrypted_body: string
      sender_id: string
      recipient_id: string
    }
  }
}

export interface Delegate {
  // targets
  textareaTarget: HTMLTextAreaElement
  // values
  institutionIdValue: string
  patientUserIdValue: string
  institutionKeyPairValue: InstitutionPublicKey
  hasTextareaTarget: Boolean
  errorMessage: ErrorMessageController
}

export class CreateMessageService {
  delegate: Delegate

  constructor(delegate: Delegate) {
    this.delegate = delegate
  }

  async create(files: File[]) {
    if (this.delegate.hasTextareaTarget) {
      const plainMessage = stripHTML(this.delegate.textareaTarget.value.trim())

      if (plainMessage.length !== 0) {
        // start message creation
        showLoader()
        const attachedFileIds = await this.uploadFiles(files)
        const encryptedBodyForInstitution = await this.encryptForInstitution(plainMessage)
        const { patientKey, encryptedKeyData, encryptedBodyForPatient } =
          await this.encryptForPatient(plainMessage)

        const payload = {
          data: {
            message: {
              encrypted_body: encryptedBodyForPatient,
              practice_id: this.delegate.institutionIdValue,
              session_key: encryptedKeyData,
              session_key_patient_public_key_external_id: patientKey.id,
            },
            recipient_data: {
              encrypted_body: encryptedBodyForInstitution,
              sender_id: this.delegate.patientUserIdValue,
              recipient_id: this.delegate.institutionIdValue,
              attached_file_ids: attachedFileIds,
            },
          },
        }

        this.postMessage(payload)
          .then((response) => {
            if (!response.ok) {
              return response.json().then((errorData) => {
                throw new Error(errorData.error || 'Unknown error')
              })
            }

            // TODO: update page with turbo?
            window.location.reload()
          })
          .catch((error) => {
            const title = ''
            const message = error.message
            this.delegate.errorMessage.set({ title, message })
          })
          .finally(hideLoader)
      }
    }
  }

  private async uploadFiles(files: File[]): Promise<string[]> {
    const filesToUpload = Array.from(files).map(this.uploadFile.bind(this))
    return Promise.all(filesToUpload)
  }

  private async uploadFile(file: File): Promise<string> {
    const institutionKey = {
      id: this.delegate.institutionKeyPairValue.id,
      institutionId: this.delegate.institutionIdValue,
      modulus: this.delegate.institutionKeyPairValue.modulus,
      publicExponent: this.delegate.institutionKeyPairValue.public_exponent,
    }
    const patientPublicKey = await getPatientKeyOnce()

    return await uploadFile({
      file,
      institutionKey,
      patientPublicKey,
    })
  }

  private async encryptForInstitution(plainMessage: string) {
    return await asymmetricEncrypt(
      plainMessage,
      this.delegate.institutionKeyPairValue.id,
      this.delegate.institutionKeyPairValue.public_exponent,
      this.delegate.institutionKeyPairValue.modulus,
    )
  }

  private async encryptForPatient(plainMessage: string) {
    const patientKey = await getPatientKeyOnce()
    const sessionKeyBytes = generateSessionKey()
    const siSessionKey = await loadSessionKey(sessionKeyBytes)
    const encryptedKeyData = await exportEncryptionKey(siSessionKey.base, patientKey.key)
    const encryptedBodyForPatient = await encryptSessionData(plainMessage, siSessionKey)

    return { patientKey, encryptedKeyData, encryptedBodyForPatient }
  }

  private async postMessage(payload: MessagePayload) {
    return fetch(`/messages?institution_id=${this.delegate.institutionIdValue}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': CSRFToken(),
      },
      body: JSON.stringify(payload),
    })
  }
}
